import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as api from "../../api/index.js";
import { IoMdArrowBack } from "react-icons/io";
import { FiShield, FiKey, FiEye, FiEyeOff, FiCheckCircle, FiChevronRight, FiMessageCircle } from "react-icons/fi";

const VerifyAndResetPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || "";
  
  // Configuration
  const ADMIN_PHONE = "2349026235719"; 
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 mins
  const REDIRECT_DELAY = 15000; // 15 seconds

  const [step, setStep] = useState("verify");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  // Logic for Delayed WhatsApp Redirect
  useEffect(() => {
    const lastRequestTime = localStorage.getItem("otp_request_time");
    const currentTime = Date.now();

    // Check if we need to trigger the redirect
    if (!lastRequestTime || currentTime - parseInt(lastRequestTime) > SESSION_TIMEOUT) {
      const timer = setTimeout(() => {
        handleWhatsAppRedirect();
      }, REDIRECT_DELAY);

      return () => clearTimeout(timer); // Cleanup if they leave page before 15s
    }
  }, []);

  const handleWhatsAppRedirect = () => {
    const whatsappMsg = encodeURIComponent(`Hi Admin, I am requesting the OTP sent to my email: ${email}`);
    localStorage.setItem("otp_request_time", Date.now().toString());
    window.open(`https://wa.me/${ADMIN_PHONE}?text=${whatsappMsg}`, '_blank');
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    try {
      await api.verifyResetCode({ email, code });
      setMessage("Identity verified. Please set your new password.");
      setStep("reset");
    } catch (err) {
      setError(err.response?.data?.error || "Invalid verification code.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    try {
      await api.resetPassword({ email, code, newPassword });
      setMessage("✅ Password secured! Redirecting to login...");
      localStorage.removeItem("otp_request_time");
      setTimeout(() => navigate("/"), 2000);
    } catch (err) {
      setError(err.response?.data?.error || "Password reset failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col relative overflow-hidden">
      <div className={`absolute top-[-10%] right-[-10%] w-96 h-96 rounded-full blur-3xl opacity-40 transition-colors duration-700 ${step === 'verify' ? 'bg-orange-100' : 'bg-green-100'}`}></div>

      <div className="p-6 relative z-10">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-900 group">
          <div className="p-2 bg-white rounded-full shadow-sm group-hover:bg-orange-50 transition-colors">
            <IoMdArrowBack size={20} />
          </div>
          <span className="text-xs font-black uppercase tracking-widest">Back</span>
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 relative z-10">
        <div className="max-w-md w-full bg-white rounded-[3rem] p-8 md:p-12 shadow-2xl shadow-gray-200/50 border border-gray-50 text-center">
          
          <div className="mb-8 relative inline-block">
            <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-inner transition-colors duration-500 ${step === 'verify' ? 'bg-orange-50 text-orange-600' : 'bg-green-50 text-green-600'}`}>
              {step === "verify" ? <FiShield size={36} /> : <FiKey size={36} />}
            </div>
          </div>

          <h1 className="text-3xl font-black tracking-tighter text-gray-900 mb-2">
            {step === "verify" ? "Security Check" : "New Password"}
          </h1>
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-8 leading-relaxed">
            {step === "verify" 
              ? `Contact our admin through that button to get your code. It will be sent to your email (${email}) immediately.` 
              : "Secure your account"}
          </p>

          {step === "verify" && (
            <div className="space-y-6">
              <button 
                onClick={handleWhatsAppRedirect}
                className="w-full py-4 border-2 border-dashed border-gray-200 rounded-2xl flex items-center justify-center gap-3 text-gray-500 hover:border-green-400 hover:text-green-600 transition-all group"
              >
                <FiMessageCircle size={20} className="group-hover:animate-bounce text-green-500" />
                <span className="text-[10px] font-black uppercase tracking-widest text-left">
                  
                    <span className="text-gray-400">Ask Admin for otp code</span>
                </span>
              </button>

              <form onSubmit={handleVerify} className="space-y-6">
                <input
                  type="text"
                  maxLength={4}
                  placeholder="0000"
                  className="w-full px-6 py-5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 transition-all text-2xl font-black tracking-[0.5em] text-center outline-none"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-5 bg-gray-900 text-white rounded-[1.5rem] font-black text-xs uppercase tracking-[0.2em] shadow-xl hover:bg-black transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  {loading ? "Verifying..." : "Confirm Identity"}
                  <FiChevronRight size={18} />
                </button>
              </form>
            </div>
          )}

          {step === "reset" && (
            <form onSubmit={handleReset} className="space-y-6">
               <div className="relative">
                  <FiKey className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="New Password"
                    className="w-full pl-12 pr-12 py-5 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-green-500 transition-all text-sm font-bold outline-none"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                    {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                  </button>
                </div>
              <button type="submit" disabled={loading} className="w-full py-5 bg-green-600 text-white rounded-[1.5rem] font-black text-xs uppercase tracking-[0.2em] shadow-xl hover:bg-green-700 transition-all flex items-center justify-center gap-2">
                {loading ? "Updating..." : "Update Password"}
                <FiCheckCircle size={18} />
              </button>
            </form>
          )}

          {(error || message) && (
            <div className={`mt-8 p-4 rounded-2xl border text-[10px] font-black uppercase tracking-wider ${message ? "bg-green-50 border-green-100 text-green-600" : "bg-red-50 border-red-100 text-red-600"}`}>
              {error || message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyAndResetPage;